import { type OpportunitySearch } from "@repo/api";
import { Text } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  FlexDiv,
  ModalSheet,
  RegionContainer,
  SectionContainer,
  type SelectListItem,
  SelectListView,
} from "@repo/ui";
import { hasItems } from "@repo/utils";
import differenceBy from "lodash/differenceBy";
import { useEffect, useState } from "react";

import { type EditModalMode } from "../utils";

interface MultiUpdateModalProps {
  title: string;
  isOpen: boolean;
  isLoading: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<EditModalMode>>;
  selectedOpportunities: OpportunitySearch[];
  onSave?: (projects: OpportunitySearch[]) => void;
  children: any;
}
export const MultiUpdateModal = ({
  isOpen,
  setIsOpen,
  isLoading,
  children,
  title,
  selectedOpportunities,
  onSave,
}: MultiUpdateModalProps) => {
  const [selected, setSelected] = useState<OpportunitySearch[]>(
    selectedOpportunities
  );

  useEffect(() => {
    setSelected(selectedOpportunities);
  }, [selectedOpportunities]);

  const list: SelectListItem[] = selectedOpportunities.map((el) => ({
    value: el.opportunityId.toString(),
    name: el.opportunityId.toString(),
    title: el.title,
    description: el.status,
  }));

  const onSelectOpportunity = (
    _checked: boolean,
    _item: SelectListItem,
    selectedKeys: string[]
  ) => {
    const newSelected = selectedOpportunities.filter((opportunity) =>
      selectedKeys.includes(opportunity.opportunityId.toString())
    );
    setSelected(newSelected);
  };

  return (
    <ModalSheet
      title={title}
      visible={isOpen}
      onClose={() => {
        setIsOpen("");
      }}
      primaryButtonProps={{
        text: t("prompt.confirm_of", {
          current: selected.length,
          total: selectedOpportunities.length,
        }),
        loading: isLoading,
        disabled: !onSave || !hasItems(selected),
        onClick: () => onSave?.(selected),
      }}
    >
      <RegionContainer>
        <SectionContainer>
          <FlexDiv alignItems="center" justifyContent="space-between">
            <Text textStyle="Heading.S">{t("label.projects_to_assign")}</Text>
          </FlexDiv>
          <SelectListView
            id="assign-opportunity-list"
            list={list}
            selectionMode="MULTI"
            selectedKeys={selected.map((el) => el.opportunityId.toString())}
            onChange={onSelectOpportunity}
            selectAll
          />
        </SectionContainer>
        <SectionContainer>{children}</SectionContainer>
      </RegionContainer>
    </ModalSheet>
  );
};

export const createSaveSucess =
  ({
    setSelectedOpportunities,
    selectedOpportunities,
    selectedProjects,
    updateToast,
    setIsOpen,
    title,
  }: {
    setSelectedOpportunities: (o: OpportunitySearch[]) => void;
    selectedOpportunities: OpportunitySearch[];
    selectedProjects: OpportunitySearch[];
    updateToast: (o: any) => void;
    setIsOpen: React.Dispatch<React.SetStateAction<EditModalMode>>;
    title?: string;
  }) =>
  () => {
    const opportunitiesMinusTheSelectedOnes = differenceBy(
      selectedOpportunities,
      selectedProjects,
      (o) => o.opportunityId
    );
    updateToast({
      id: selectedProjects.map((el) => el.opportunityId).join(","),
      title: title ?? t("prompt.project_saved"),
    });
    setSelectedOpportunities(opportunitiesMinusTheSelectedOnes);
    if (opportunitiesMinusTheSelectedOnes.length === 0) {
      setIsOpen("");
    }
  };
